<template>
  <div>
    <!-- Hero -->
    <section class="hero container page-top">
      <span class="pill">{{ t('hero.eyebrow') }}</span>
      <h1 class="display">
        {{ t('hero.h1a') }}<br>
        <span class="italic-accent">{{ t('hero.h1b') }}</span>
      </h1>
      <p class="lead intro">{{ t('hero.sub') }}</p>
      <div class="buttons">
        <PillButton to="/contact">{{ t('cta') }}</PillButton>
        <PillButton to="/products" variant="secondary">{{ t('cta2') }}</PillButton>
        <PillButton :href="bookingUrl" target="_blank" rel="noopener noreferrer" variant="outline">{{ copy.bookCall }}</PillButton>
      </div>
    </section>

    <!-- Product screenshot -->
    <section class="container shot-section">
      <div class="shot-panel">
        <div class="shot">
          <ImageSlot v-if="heroImage" :src="heroImage" :alt="t('hero.h1a') + ' ' + t('hero.h1b')" />
          <HeroMock v-else />
        </div>
      </div>
    </section>

    <!-- Solutions -->
    <section class="section container">
      <div class="head">
        <div class="head-text">
          <h2 class="h2">{{ t('solutions.title') }}</h2>
          <p class="sub">{{ t('solutions.sub') }}</p>
        </div>
        <PillButton to="/products" variant="outline" size="md">{{ t('cta2') }} ({{ solutions.length }})</PillButton>
      </div>
      <div class="grid">
        <SolutionCard v-for="solution in featured" :key="solution.slug" :solution="solution" />
      </div>
    </section>

    <!-- How we work -->
    <section v-if="steps.length" class="section container">
      <div class="process">
        <h2 class="h2">{{ t('process.title') }}</h2>
        <ol class="steps">
          <li v-for="(step, index) in steps" :key="index" class="step">
            <span class="num">{{ index + 1 }}</span>
            <h3 class="step-title">{{ step.title }}</h3>
            <p class="step-body">{{ step.body }}</p>
          </li>
        </ol>
      </div>
    </section>

    <!-- Selected work: hidden until real case studies are added in the CMS -->
    <section v-if="work.length" class="section container">
      <h2 class="h2 work-title">{{ t('work.title') }}</h2>
      <div class="work-grid">
        <component
          :is="item.case_study_slug ? 'router-link' : 'div'"
          v-for="(item, index) in work"
          :key="index"
          v-bind="item.case_study_slug ? { to: to(`/case-studies/${item.case_study_slug}`) } : {}"
          class="work-card"
        >
          <div class="work-image">
            <ImageSlot :src="item.image || ''" :alt="item.title" :label="item.client || item.title" :tint="tints[index % tints.length]" />
          </div>
          <span class="work-label">{{ [item.client, item.industry].filter(Boolean).join(' · ') }}</span>
          <span class="work-name">{{ item.title }}</span>
        </component>
      </div>
    </section>

    <!-- FAQ -->
    <section v-if="faqs.length" class="section faq-section">
      <h2 class="h2 faq-title">{{ t('faq.title') }}</h2>
      <FaqAccordion :items="faqs" />
    </section>

    <CtaBand />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import PillButton from '../components/PillButton.vue'
import SolutionCard from '../components/SolutionCard.vue'
import FaqAccordion from '../components/FaqAccordion.vue'
import ImageSlot from '../components/ImageSlot.vue'
import HeroMock from '../components/HeroMock.vue'
import CtaBand from '../components/CtaBand.vue'
import { useLocale } from '../composables/useLocale'
import { solutions, site } from '../data'
import { BOOKING_URL as bookingUrl, CONVERSION_COPY } from '../config/site'

const { locale, t, list, to } = useLocale()
const copy = computed(() => CONVERSION_COPY[locale.value] || CONVERSION_COPY.en)

const featured = computed(() => solutions.slice(0, 6))
const steps = computed(() => list('process.steps'))
const faqs = computed(() => list('faq.items'))
const work = computed(() => list('work.items').filter((item) => item && item.title))
// A screenshot uploaded in the CMS wins; otherwise the bundled dashboard shows.
const heroImage = site.shared?.hero_image || '/hero-dashboard.png'

const tints = ['var(--tint-1)', 'var(--tint-2)', 'var(--tint-3)', 'var(--tint-4)']
</script>

<style scoped>
.hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.hero .pill {
  margin-bottom: 28px;
}

.intro {
  margin: 28px 0 34px;
  max-width: 40ch;
}

.buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
}

.shot-section {
  padding-top: clamp(40px, 6vw, 64px);
}

.shot-panel {
  background: var(--dark);
  border-radius: var(--radius-panel);
  padding: clamp(20px, 4vw, 48px) clamp(20px, 4vw, 48px) 0;
  overflow: hidden;
}

.shot {
  height: clamp(240px, 42vw, 560px);
  border-radius: 14px 14px 0 0;
  overflow: hidden;
  background: #fff;
}

.shot :deep(img) {
  object-position: top left;
}

.head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 40px;
}

.head-text {
  max-width: 640px;
}

.head-text .h2 {
  margin-bottom: 16px;
}

.sub {
  font-size: 18px;
  line-height: 1.6;
  color: var(--muted);
  text-wrap: pretty;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 320px), 1fr));
  gap: 16px;
}

.process {
  background: var(--dark);
  color: #fff;
  border-radius: var(--radius-panel);
  padding: clamp(40px, 6vw, 80px) clamp(24px, 4vw, 56px);
}

.process .h2 {
  margin-bottom: 48px;
}

.steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 240px), 1fr));
  gap: 16px;
}

.step {
  background: var(--dark-inner);
  border-radius: var(--radius-inner);
  padding: 28px;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.num {
  font-family: 'Newsreader', serif;
  font-style: italic;
  font-size: 44px;
  line-height: 1;
  color: var(--gold-on-dark);
}

.step-title {
  font-family: var(--font-display);
  font-weight: 400;
  font-size: 28px;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.step-body {
  font-size: 16px;
  line-height: 1.6;
  color: var(--on-dark-muted);
  text-wrap: pretty;
}

.work-title {
  margin-bottom: 40px;
}

.work-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 420px), 1fr));
  gap: 16px;
}

.work-card {
  background: var(--surface);
  border-radius: var(--radius-card);
  padding: 14px 14px 28px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
  color: var(--ink);
}

a.work-card:hover {
  background: var(--surface-hover);
  color: var(--ink);
}

.work-image {
  height: clamp(220px, 28vw, 380px);
  border-radius: 16px;
  overflow: hidden;
}

.work-label {
  padding: 0 12px;
  font-size: 14px;
  color: var(--tertiary);
}

.work-name {
  padding: 0 12px;
  font-family: var(--font-display);
  font-size: 28px;
  line-height: 1.2;
}

.faq-section {
  max-width: 900px;
  margin-inline: auto;
  padding-inline: var(--gutter);
}

.faq-title {
  text-align: center;
  margin-bottom: 36px;
}
</style>
